"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("@emotion/react");
const get_1 = __importDefault(require("lodash/get"));
const react_2 = require("react");
const react_d3_utils_1 = require("react-d3-utils");
const fa_1 = require("react-icons/fa");
const react_plot_1 = require("react-plot");
const ChartContext_1 = require("../../context/ChartContext");
const Button_1 = __importDefault(require("../../elements/Button"));
const Input_1 = __importDefault(require("../../elements/Input"));
const Label_1 = __importDefault(require("../../elements/Label"));
const Alert_1 = require("../../elements/popup/Alert");
const useSpectraPerNucleus_1 = __importDefault(require("../../hooks/useSpectraPerNucleus"));
const export_1 = require("../../utility/export");
const getSpectraObjectPaths_1 = require("../../utility/getSpectraObjectPaths");
const padding = 15;
const svgId = 'multiple-analysis-svg';
function prepareAnalysisData(analysisData, columnKey) {
    const valueKey = analysisData.options.columns?.[columnKey]?.valueKey;
    const spectraAnalysis = {};
    if (valueKey) {
        for (const record of analysisData.values) {
            const data = record?.[columnKey];
            spectraAnalysis[data.SID] = data[valueKey];
        }
    }
    return spectraAnalysis;
}
function usePlotData(analysisData, options) {
    const spectra = (0, useSpectraPerNucleus_1.default)();
    const { yPath, xPath, paths } = options;
    const xPathKeys = paths?.[xPath];
    const yPathKeys = paths?.[yPath];
    let xData;
    let yData;
    const analysisColumns = getAnalysisColumns(analysisData);
    if (analysisColumns.includes(xPath)) {
        xData = prepareAnalysisData(analysisData, xPath);
    }
    if (analysisColumns.includes(yPath)) {
        yData = prepareAnalysisData(analysisData, yPath);
    }
    return spectra.map((spectrum, index) => {
        const x = xData
            ? xData[spectrum.id]
            : (0, get_1.default)(spectrum, xPathKeys, index);
        const y = yData
            ? yData[spectrum.id]
            : (0, get_1.default)(spectrum, yPathKeys, index);
        return {
            x,
            y,
        };
    });
}
function getAnalysisColumns(spectraAnalysisData) {
    return Object.keys(spectraAnalysisData.options.columns);
}
function getAnalysisColumnsPaths(spectraAnalysisData) {
    const datalist = getAnalysisColumns(spectraAnalysisData);
    const paths = {};
    for (const key of datalist) {
        paths[key] = [key];
    }
    return {
        datalist,
        paths,
    };
}
function AnalysisChart(props) {
    const { spectraAnalysisData } = props;
    const { data } = (0, ChartContext_1.useChartData)();
    const alert = (0, Alert_1.useAlert)();
    const chartParentRef = (0, react_2.useRef)(null);
    const [plotOptions, setPlotOptions] = (0, react_2.useState)({
        xPath: '',
        yPath: '',
    });
    const { datalist, paths } = (0, react_2.useMemo)(() => {
        const { datalist: xDataList, paths: xPaths } = (0, getSpectraObjectPaths_1.getSpectraObjectPaths)(data, [
            'info',
            'meta',
            'customInfo',
        ]);
        const { datalist: yDataList, paths: yPaths } = getAnalysisColumnsPaths(spectraAnalysisData);
        const datalist = yDataList.concat(xDataList);
        const paths = { ...yPaths, ...xPaths };
        return { datalist, paths };
    }, [data, spectraAnalysisData]);
    function handleChangeKey(event) {
        const { name, value } = event.target;
        setPlotOptions((prevOptions) => ({ ...prevOptions, [name]: value }));
    }
    function handleCopy() {
        if (chartParentRef.current) {
            (0, export_1.copyPNGToClipboard)(chartParentRef.current, svgId, (0, react_1.css)({ text: { fill: 'black' } }));
            alert.success('Chart copied to clipboard');
        }
    }
    const plotData = usePlotData(spectraAnalysisData, { ...plotOptions, paths });
    const xLabel = paths?.[plotOptions.xPath]?.at(-1) || '';
    const yLabel = paths?.[plotOptions.yPath]?.at(-1) || '';
    return ((0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsxs)("div", { style: { display: 'flex', padding: '5px' }, children: [(0, jsx_runtime_1.jsx)(Label_1.default, { title: "X ", children: (0, jsx_runtime_1.jsx)(Input_1.default, { name: "xPath", style: {
                                input: { padding: '5px' },
                            }, value: plotOptions.xPath, datalist: datalist, onChange: handleChangeKey }) }), (0, jsx_runtime_1.jsx)(Label_1.default, { title: "Y ", style: { container: { paddingLeft: '5px' } }, children: (0, jsx_runtime_1.jsx)(Input_1.default, { name: "yPath", style: {
                                input: { padding: '5px' },
                            }, value: plotOptions.yPath, datalist: datalist, onChange: handleChangeKey }) }), (0, jsx_runtime_1.jsx)(Button_1.default.BarButton, { toolTip: "Copy chart to clipboard", onClick: handleCopy, children: (0, jsx_runtime_1.jsx)(fa_1.FaFileExport, {}) })] }), (0, jsx_runtime_1.jsx)("div", { style: {
                    width: '100%',
                    position: 'relative',
                    height: '220px',
                }, ref: chartParentRef, children: (0, jsx_runtime_1.jsx)(react_d3_utils_1.ResponsiveChart, { children: ({ width, height }) => ((0, jsx_runtime_1.jsxs)(react_plot_1.Plot, { width: width, height: height, svgClassName: svgId, svgId: svgId, margin: {
                            top: padding,
                            right: padding,
                            left: padding,
                            bottom: padding,
                        }, children: [(0, jsx_runtime_1.jsx)(react_plot_1.LineSeries, { data: plotData, xAxis: "x", yAxis: "y" }), (0, jsx_runtime_1.jsx)(react_plot_1.Axis, { id: "x", label: `X ( ${xLabel} )`, position: "bottom", tickLabelStyle: { fontSize: '0.6rem' }, labelStyle: { fontSize: '0.6rem' } }), (0, jsx_runtime_1.jsx)(react_plot_1.Axis, { id: "y", label: `Y ( ${yLabel} )`, position: "left", tickLabelStyle: { fontSize: '0.6rem' }, labelStyle: { fontSize: '0.7rem' } })] })) }) })] }));
}
exports.default = AnalysisChart;
